import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { Icon } from '../icon';
import { SelectionBox } from './components/bounding-box-selector';
import { CloseIcon } from './components/close-icon';
import { DocViewerLoader } from './components/doc-viewer-loader';
import { DownloadIcon } from './components/download-icon';
import { ExtractFactFromImagePopup } from './components/extract-fact-from-image-popup';
import { ExtractFactsFromSelectionButton } from './components/extract-facts-from-selection-button';
import { FullDocLink } from './components/full-doc-link';
import { ImageRenderer } from './components/image-renderer';
import { PDFDocumentRenderer } from './components/pdf-renderer';
import { SearchBar } from './components/search-bar';
import { SearchButton } from './components/search-button';
import { ShowFactsIcon } from './components/show-facts';
import { Zoom } from './components/zoom';
import { DEFAULT_PAGE_HEIGHT, DEFAULT_PAGE_WIDTH } from './constants';
import { useVisiblePages } from './use-visible-pages';
import { Button } from '../shadcn/button';
import { ButtonGroup } from '../shadcn/button-group';
import { useGetDoc } from '@/api/queries/useGetDoc';
import { useGetDocChunkMatchesV2 } from '@/api/queries/useGetDocChunkMatchesV2';
import { BoundingBox, BoundingBoxWithId } from '@/types';

export interface RendererBaseProps {
  docId: string;

  getPageDimensions: (pageNumber: number) => { height: number; width: number };
  visiblePages: number[];
  getPagePosition: (pageNumber: number) => number;
  totalHeight: number;
  maxPageWidth?: number;

  highlights: BoundingBoxWithId[];
  highlightedFactId?: string | null;
  onHighlightClick?: (id: string) => void;
  highlightedChunkId?: string | null;

  isSelectionMode?: boolean;
  onSelectionComplete?: (
    boundingBox: BoundingBox,
    imageData: string,
    selectionBox: SelectionBox,
    pageNumber: number,
  ) => void;
  maintainSelection?: boolean;
  activeSelection?: { box: SelectionBox; pageNumber: number } | null;
}

export interface PDFViewerRef {
  clearSearch: () => void;
}

interface PDFViewerProps {
  docId: string;
  highlights: BoundingBoxWithId[];
  setHighlightedFactId?: (id: string) => void;
  highlightedFactId?: string | null;
  onHighlightClick?: (id: string) => void;
  onViewerClick?: () => void;
  closeViewer?: () => void;
  hideFeatureButtons?: boolean;
  hideSnippingTool?: boolean;
  showFacts?: boolean;
  setShowFacts?: (show: boolean) => void;
  fileName?: string;
  initialSearchQuery?: string;
  fileUrl?: string;
  fileMetadata?: {
    name?: string;
    extension?: string;
    numberOfPages?: number | null;
    height?: number | null;
    width?: number | null;
    hasBeenConvertedToImages?: boolean;
    pageDimensions?: { width: number; height: number }[] | null;
  };
}

export const PDFViewer = forwardRef<PDFViewerRef, PDFViewerProps>(
  (
    {
      docId,
      highlights,
      closeViewer,
      highlightedFactId,
      setHighlightedFactId,
      onHighlightClick,
      onViewerClick,
      hideFeatureButtons,
      hideSnippingTool,
      showFacts,
      setShowFacts,
      fileName,
      initialSearchQuery,
      fileUrl,
      fileMetadata,
    },
    ref,
  ) => {
    const { matterId } = useParams();

    // Selection mode state
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [popupData, setPopupData] = useState<{
      isOpen: boolean;
      imageBase64: string;
      boundingBox: BoundingBox | null;
      anchorPosition: { x: number; y: number; pageNumber: number } | null;
      side: 'left' | 'right';
    }>({
      isOpen: false,
      imageBase64: '',
      boundingBox: null,
      anchorPosition: null,
      side: 'left',
    });
    const [activeSelection, setActiveSelection] = useState<{ box: SelectionBox; pageNumber: number } | null>(null);

    // Search state
    const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');
    const [showSearchBar, setShowSearchBar] = useState(!!initialSearchQuery);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const [highlightedChunkId, setHighlightedChunkId] = useState<string | null>(null);

    // Zoom state - start with 1, will be updated when doc loads
    const [zoom, setZoom] = useState(1);
    const isUpdatingZoom = useRef(false);
    const zoomRef = useRef(1);
    const pendingScrollRef = useRef<{ top: number; left: number } | null>(null);
    const pendingAutoZoomLayoutRef = useRef(false);
    const pendingAutoZoomHighlightSyncRef = useRef(false);
    const hasFinishedInitialLayoutRef = useRef(false);
    const resizeAutoZoomTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastObservedContainerWidthRef = useRef<number | null>(null);

    // Outer container
    const containerRef = useRef<HTMLDivElement>(null);

    // Get info about the doc
    const [loadedFileUrlPageCount, setLoadedFileUrlPageCount] = useState<number | null>(null);
    const [loadedPdfPageDimensions, setLoadedPdfPageDimensions] = useState<
      Record<number, { width: number; height: number }>
    >({});

    const shouldFetchDoc = !fileUrl;
    const { data: doc, isLoading: isFetchedDocLoading } = useGetDoc(shouldFetchDoc ? matterId : undefined, docId);
    const file = fileMetadata ?? doc?.file ?? (fileUrl ? {} : undefined);
    const hasBeenConvertedToImages = file?.hasBeenConvertedToImages ?? false;
    const numPages = file?.numberOfPages ?? loadedFileUrlPageCount ?? 0;
    const isDocLoading = shouldFetchDoc && isFetchedDocLoading;
    const hasLoadedPdfPageDimensions = Object.keys(loadedPdfPageDimensions).length > 0;

    // Get search matches
    const { data: searchMatches } = useGetDocChunkMatchesV2(matterId, docId, fileUrl ? '' : searchQuery);

    // Combine fact highlights with search highlights
    const { allHighlights, searchChunks } = useMemo(() => {
      // When searching, only show search highlights
      const combinedHighlights = searchQuery ? [] : [...highlights];
      const chunks: { chunkId: string; boundingBoxIds: string[] }[] = [];

      if (searchMatches?.chunks) {
        searchMatches.chunks.forEach((chunk) => {
          const chunkBoundingBoxIds: string[] = [];
          if (chunk.boundingBoxes) {
            chunk.boundingBoxes.forEach((box, boxIndex) => {
              const searchId = `search-${chunk.id}-${box.pageNumber}-${boxIndex}`;
              chunkBoundingBoxIds.push(searchId);
              combinedHighlights.push({
                ...box,
                id: searchId,
              });
            });
          }
          if (chunkBoundingBoxIds.length > 0) {
            chunks.push({ chunkId: chunk.id, boundingBoxIds: chunkBoundingBoxIds });
          }
        });
      }

      return { allHighlights: combinedHighlights, searchChunks: chunks };
    }, [highlights, searchMatches, searchQuery]);

    // Calculate total search matches (chunks, not bounding boxes)
    const totalSearchMatches = searchChunks.length;

    // Reset current match index when search results change
    useEffect(() => {
      if (searchQuery) {
        setCurrentMatchIndex(0);
        setHighlightedChunkId(null);
      }
    }, [searchMatches]);

    // Track current index when navigating through a fact's multiple page highlights
    const [currentFactHighlightIndex, setCurrentFactHighlightIndex] = useState(0);

    // Compute sorted unique highlights for the currently highlighted fact
    const factHighlightsForCurrentFact = useMemo(() => {
      if (!highlightedFactId) return [];

      // Get all highlights for this fact, sorted by page then by top position
      const factHighlights = allHighlights
        .filter((h) => h.id === highlightedFactId)
        .sort((a, b) => {
          const pageCompare = a.pageNumber - b.pageNumber;
          if (pageCompare !== 0) return pageCompare;
          return a.top - b.top;
        });

      return factHighlights;
    }, [highlightedFactId, allHighlights]);

    // Get unique pages for the current fact (for display purposes)
    const uniquePagesForCurrentFact = useMemo(() => {
      const pages = new Set(factHighlightsForCurrentFact.map((h) => h.pageNumber));
      return Array.from(pages).sort((a, b) => a - b);
    }, [factHighlightsForCurrentFact]);

    const hasHighlightedFact = useMemo(
      () => (highlightedFactId ? allHighlights.some((highlight) => highlight.id === highlightedFactId) : false),
      [allHighlights, highlightedFactId],
    );

    // Reset highlight index when fact changes
    useEffect(() => {
      setCurrentFactHighlightIndex(0);
    }, [highlightedFactId]);

    // Calculate max width across all pages for default zoom
    const getMaxPageWidth = useCallback(() => {
      if (!file || numPages === 0) return DEFAULT_PAGE_WIDTH;

      let maxWidth = 0;

      if (file.pageDimensions) {
        for (let i = 0; i < numPages; i++) {
          const pageDim = file.pageDimensions[i];
          if (pageDim) {
            const scaleRatio = pageDim.height / DEFAULT_PAGE_HEIGHT;
            const normalizedWidth = pageDim.width / scaleRatio;
            maxWidth = Math.max(maxWidth, normalizedWidth);
          }
        }
      } else if (hasLoadedPdfPageDimensions) {
        for (let i = 1; i <= numPages; i++) {
          const pageDim = loadedPdfPageDimensions[i];
          if (pageDim) {
            maxWidth = Math.max(maxWidth, pageDim.width);
          }
        }
      } else {
        const scaleRatio = (file.height ?? DEFAULT_PAGE_HEIGHT) / DEFAULT_PAGE_HEIGHT;
        maxWidth = (file.width ?? DEFAULT_PAGE_WIDTH) / scaleRatio;
      }

      return maxWidth || DEFAULT_PAGE_WIDTH;
    }, [file, numPages, hasLoadedPdfPageDimensions, loadedPdfPageDimensions]);

    const getPageDimensions = useCallback(
      (pageNumber: number) => {
        if (file?.pageDimensions && file.pageDimensions[pageNumber - 1]) {
          const pageDim = file.pageDimensions[pageNumber - 1];
          const scaleRatio = pageDim.height / DEFAULT_PAGE_HEIGHT;
          return {
            height: DEFAULT_PAGE_HEIGHT * zoom,
            width: (pageDim.width / scaleRatio) * zoom,
          };
        }

        const loadedPageDim = loadedPdfPageDimensions[pageNumber];
        if (loadedPageDim) {
          return {
            height: loadedPageDim.height * zoom,
            width: loadedPageDim.width * zoom,
          };
        }

        const scaleRatio = (file?.height ?? DEFAULT_PAGE_HEIGHT) / DEFAULT_PAGE_HEIGHT;
        return {
          height: DEFAULT_PAGE_HEIGHT * zoom,
          width: ((file?.width ?? DEFAULT_PAGE_WIDTH) / scaleRatio) * zoom,
        };
      },
      [file, loadedPdfPageDimensions, zoom],
    );

    // Precompute page positions, total height, and max width in a single pass
    const { totalHeight, maxPageWidth, pagePositions } = useMemo(() => {
      const positions: number[] = [0]; // positions[0] is unused, positions[1] = position of page 1
      let height = 0;
      let maxWidth = 0;

      for (let i = 1; i <= numPages; i++) {
        positions[i] = height;
        const pageDims = getPageDimensions(i);
        height += pageDims.height + 10; // 10px gap between pages
        maxWidth = Math.max(maxWidth, pageDims.width);
      }

      return { totalHeight: height, maxPageWidth: maxWidth, pagePositions: positions };
    }, [numPages, getPageDimensions]);

    const getOptimalZoom = useCallback(() => {
      if (!containerRef.current || !file) return 1;

      const container = containerRef.current;
      const availableWidth = container.clientWidth - 40; // Leave some padding
      const maxWidth = getMaxPageWidth();
      const calculatedZoom = availableWidth / maxWidth;
      const finalZoom = Math.min(calculatedZoom, 4); // Cap at 4x zoom

      return finalZoom;
    }, [getMaxPageWidth, file]);

    useEffect(() => {
      zoomRef.current = zoom;
    }, [zoom]);

    const calculateViewportPreservingScroll = useCallback(
      (prevZoom: number, nextZoom: number) => {
        const container = containerRef.current;
        if (!container) return null;

        const viewportCenterY = container.scrollTop + container.clientHeight / 2;
        const viewportCenterX = container.scrollLeft + container.clientWidth / 2;

        // Keep the same point in the document centered, while accounting for
        // the fixed 10px inter-page gaps that do not scale with zoom.
        const pageHeightWithGap = DEFAULT_PAGE_HEIGHT * prevZoom + 10;
        const approxPagePosition = viewportCenterY / pageHeightWithGap;
        const gapsAbove = Math.floor(approxPagePosition);
        const totalGapHeight = gapsAbove * 10;
        const contentY = viewportCenterY - totalGapHeight;
        const normalizedContentY = contentY / prevZoom;

        const newContentY = normalizedContentY * nextZoom;
        const newScrollTop = newContentY + totalGapHeight - container.clientHeight / 2;

        const prevContentWidth = container.scrollWidth;
        const viewportCenterRatioX =
          prevContentWidth > 0 ? Math.min(Math.max(viewportCenterX / prevContentWidth, 0), 1) : 0.5;
        const nextContentWidth = Math.max(getMaxPageWidth() * nextZoom + 40, container.clientWidth);
        const newViewportCenterX = viewportCenterRatioX * nextContentWidth;
        const newScrollLeft = newViewportCenterX - container.clientWidth / 2;

        return { top: Math.max(0, newScrollTop), left: Math.max(0, newScrollLeft) };
      },
      [getMaxPageWidth],
    );

    const applyAutoZoom = useCallback(
      (targetZoom: number) => {
        const currentZoom = zoomRef.current;
        const zoomChanged = Math.abs(targetZoom - currentZoom) > 0.001;

        if (!zoomChanged) return;

        pendingAutoZoomLayoutRef.current = true;
        if (hasFinishedInitialLayoutRef.current) {
          pendingScrollRef.current = calculateViewportPreservingScroll(currentZoom, targetZoom);
          pendingAutoZoomHighlightSyncRef.current = Boolean(highlightedFactId);
        }

        isUpdatingZoom.current = true;
        setZoom(targetZoom);

        // Prevent a resize->zoom->resize feedback loop while the DOM settles.
        setTimeout(() => {
          isUpdatingZoom.current = false;
        }, 100);
      },
      [calculateViewportPreservingScroll, highlightedFactId],
    );

    const scheduleAutoZoom = useCallback(
      (delayMs: number) => {
        if (resizeAutoZoomTimeoutRef.current) {
          clearTimeout(resizeAutoZoomTimeoutRef.current);
        }

        resizeAutoZoomTimeoutRef.current = setTimeout(() => {
          resizeAutoZoomTimeoutRef.current = null;
          applyAutoZoom(getOptimalZoom());
        }, delayMs);
      },
      [applyAutoZoom, getOptimalZoom],
    );

    const handleZoom = useCallback(
      (delta: number) => {
        if (!containerRef.current) return;

        const prevZoom = zoom;
        // Calculate new zoom using multiplicative scaling for consistent feel at all zoom levels
        // delta is treated as a percentage: positive = zoom in, negative = zoom out
        const zoomFactor = 1 + delta;
        const newZoom = Math.max(0.2, Math.min(zoom * zoomFactor, 3));
        pendingScrollRef.current = calculateViewportPreservingScroll(prevZoom, newZoom);
        setZoom(newZoom);
      },
      [calculateViewportPreservingScroll, zoom],
    );

    const handleZoomIn = () => handleZoom(0.1);
    const handleZoomOut = () => handleZoom(-0.1);

    const handlePdfPageLoadSuccess = useCallback(
      (pageNumber: number, dimensions: { width: number; height: number }) => {
        if (file?.pageDimensions?.[pageNumber - 1]) return;

        setLoadedPdfPageDimensions((prev) => {
          const existing = prev[pageNumber];
          if (
            existing &&
            Math.abs(existing.width - dimensions.width) < 0.01 &&
            Math.abs(existing.height - dimensions.height) < 0.01
          ) {
            return prev;
          }

          return { ...prev, [pageNumber]: dimensions };
        });
      },
      [file?.pageDimensions],
    );

    // Update zoom when document loads and container resizes, but only if user hasn't manually changed it
    const handleResize = useCallback(
      (entries: ResizeObserverEntry[]) => {
        const container = containerRef.current;

        if (!container || isUpdatingZoom.current) return;

        for (const entry of entries) {
          const width = Math.round(entry.contentRect.width);
          const previousWidth = lastObservedContainerWidthRef.current;
          lastObservedContainerWidthRef.current = width;

          if (width > 100 && previousWidth !== null && width !== previousWidth) {
            scheduleAutoZoom(hasFinishedInitialLayoutRef.current ? 120 : 0);
          }
        }
      },
      [scheduleAutoZoom],
    );

    // Get optimal zoom when file loads
    useEffect(() => {
      if (!containerRef.current || !file) return;

      const container = containerRef.current;
      const resizeObserver = new ResizeObserver(handleResize);
      lastObservedContainerWidthRef.current = Math.round(container.clientWidth);
      resizeObserver.observe(container);

      // Initial zoom calculation
      if (container.clientWidth > 100) {
        scheduleAutoZoom(0);
      }

      return () => {
        resizeObserver.disconnect();
      };
    }, [file, scheduleAutoZoom]);

    // Add trackpad zoom functionality
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const handleWheel = (event: WheelEvent) => {
        // Check if this is a zoom gesture (Ctrl/Cmd key pressed or trackpad pinch)
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();

          // Calculate zoom delta based on wheel direction
          const delta = -event.deltaY * 0.01;
          handleZoom(delta);
        }
      };

      container.addEventListener('wheel', handleWheel, { passive: false });

      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }, [handleZoom]);

    const visiblePages = useVisiblePages(containerRef, numPages, getPageDimensions);
    const [debouncedVisiblePages] = useDebounce(visiblePages, 30);

    // O(1) lookup using precomputed positions instead of O(n) loop
    const getPagePosition = useCallback((pageNumber: number) => pagePositions[pageNumber] ?? 0, [pagePositions]);

    // Handle scrolling to a highlight when the scrollToHighlightId changes
    const calculateScrollPosition = useCallback(
      (highlight: BoundingBoxWithId) => {
        const pageDims = getPageDimensions(highlight.pageNumber);

        // Calculate the scroll position using the top and the page number
        // Subtract 15% of page height for padding above the highlight
        return highlight.top * pageDims.height + getPagePosition(highlight.pageNumber) - pageDims.height * 0.15;
      },
      [getPageDimensions, getPagePosition],
    );

    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const scrollToHighlightRef = useRef<(id: string) => boolean>(() => false);
    const lastAutoScrolledHighlightRef = useRef<string | null>(null);
    const lastHighlightedFactAvailableRef = useRef(false);

    const scrollHighlightNow = useCallback(
      (id: string) => {
        const container = containerRef.current;
        if (!container) return false;

        const highlight = allHighlights.find((h) => h.id === id);
        if (!highlight) return false;

        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        if (setHighlightedFactId) {
          setHighlightedFactId(id);
        }

        const scrollY = calculateScrollPosition(highlight);
        container.scrollTo({ top: Math.max(0, scrollY), behavior: 'smooth' });
        return true;
      },
      [allHighlights, calculateScrollPosition, setHighlightedFactId],
    );

    const scrollToHighlight = useCallback(
      (id: string) => {
        if (!containerRef.current) return false;

        const highlight = allHighlights.find((h) => h.id === id);
        if (!highlight) return false;

        // Debounce scroll requests so repeated updates (for example during resize)
        // collapse into a single final scroll position.
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
          if (setHighlightedFactId) {
            setHighlightedFactId(id);
          }

          const scrollY = calculateScrollPosition(highlight);
          containerRef.current?.scrollTo({ top: Math.max(0, scrollY), behavior: 'smooth' });
        }, 25);
        return true;
      },
      [calculateScrollPosition, setHighlightedFactId, allHighlights],
    );

    useEffect(() => {
      scrollToHighlightRef.current = scrollToHighlight;
    }, [scrollToHighlight]);

    useLayoutEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      if (pendingScrollRef.current) {
        container.scrollTop = pendingScrollRef.current.top;
        container.scrollLeft = pendingScrollRef.current.left;
        pendingScrollRef.current = null;
      }

      if (pendingAutoZoomLayoutRef.current) {
        pendingAutoZoomLayoutRef.current = false;
        if (!hasFinishedInitialLayoutRef.current) {
          if (!highlightedFactId || scrollHighlightNow(highlightedFactId)) {
            lastAutoScrolledHighlightRef.current = highlightedFactId ?? null;
            lastHighlightedFactAvailableRef.current = hasHighlightedFact;
            hasFinishedInitialLayoutRef.current = true;
          }
        } else if (pendingAutoZoomHighlightSyncRef.current && highlightedFactId) {
          if (scrollHighlightNow(highlightedFactId)) {
            lastAutoScrolledHighlightRef.current = highlightedFactId;
            lastHighlightedFactAvailableRef.current = hasHighlightedFact;
          }
        }
        pendingAutoZoomHighlightSyncRef.current = false;
      }
    }, [zoom, highlightedFactId, hasHighlightedFact, scrollHighlightNow]);

    useEffect(() => {
      if (hasFinishedInitialLayoutRef.current) return;
      if (pendingAutoZoomLayoutRef.current) return;
      if (!doc?.file) return;

      if (!highlightedFactId || scrollHighlightNow(highlightedFactId)) {
        lastAutoScrolledHighlightRef.current = highlightedFactId ?? null;
        lastHighlightedFactAvailableRef.current = hasHighlightedFact;
        hasFinishedInitialLayoutRef.current = true;
      }
    }, [doc?.file, highlightedFactId, hasHighlightedFact, scrollHighlightNow]);

    // Scroll only when the requested highlight changes or when it first becomes available.
    // Manual zoom updates should preserve the user's viewport instead of jumping back here.
    useEffect(() => {
      if (!hasFinishedInitialLayoutRef.current) return;
      if (!highlightedFactId) {
        lastAutoScrolledHighlightRef.current = null;
        lastHighlightedFactAvailableRef.current = false;
        return;
      }

      const highlightChanged = lastAutoScrolledHighlightRef.current !== highlightedFactId;
      const highlightBecameAvailable = hasHighlightedFact && !lastHighlightedFactAvailableRef.current;
      lastHighlightedFactAvailableRef.current = hasHighlightedFact;

      if (!hasHighlightedFact || (!highlightChanged && !highlightBecameAvailable)) return;

      if (scrollToHighlightRef.current(highlightedFactId)) {
        lastAutoScrolledHighlightRef.current = highlightedFactId;
      }
    }, [highlightedFactId, hasHighlightedFact]);

    useEffect(() => {
      hasFinishedInitialLayoutRef.current = false;
      pendingAutoZoomLayoutRef.current = false;
      pendingAutoZoomHighlightSyncRef.current = false;
      pendingScrollRef.current = null;
      lastObservedContainerWidthRef.current = null;
      lastAutoScrolledHighlightRef.current = null;
      lastHighlightedFactAvailableRef.current = false;

      if (resizeAutoZoomTimeoutRef.current) {
        clearTimeout(resizeAutoZoomTimeoutRef.current);
        resizeAutoZoomTimeoutRef.current = null;
      }

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      setLoadedFileUrlPageCount(null);
      setLoadedPdfPageDimensions({});
    }, [docId, fileUrl]);

    useEffect(() => {
      return () => {
        if (resizeAutoZoomTimeoutRef.current) {
          clearTimeout(resizeAutoZoomTimeoutRef.current);
        }
      };
    }, []);

    // Navigate to the next highlight within the current fact's pages (loops around)
    const handleNextFactHighlight = useCallback(() => {
      if (uniquePagesForCurrentFact.length === 0) return;

      const nextIndex = (currentFactHighlightIndex + 1) % uniquePagesForCurrentFact.length;
      setCurrentFactHighlightIndex(nextIndex);

      // Find the first highlight on the target page
      const targetPage = uniquePagesForCurrentFact[nextIndex];
      const targetHighlight = factHighlightsForCurrentFact.find((h) => h.pageNumber === targetPage);

      if (targetHighlight && containerRef.current) {
        const scrollY = calculateScrollPosition(targetHighlight);
        containerRef.current.scrollTo({ top: Math.max(0, scrollY), behavior: 'smooth' });
      }
    }, [uniquePagesForCurrentFact, currentFactHighlightIndex, factHighlightsForCurrentFact, calculateScrollPosition]);

    // Navigate to the previous highlight within the current fact's pages (loops around)
    const handlePreviousFactHighlight = useCallback(() => {
      if (uniquePagesForCurrentFact.length === 0) return;

      const prevIndex =
        currentFactHighlightIndex <= 0 ? uniquePagesForCurrentFact.length - 1 : currentFactHighlightIndex - 1;
      setCurrentFactHighlightIndex(prevIndex);

      // Find the first highlight on the target page
      const targetPage = uniquePagesForCurrentFact[prevIndex];
      const targetHighlight = factHighlightsForCurrentFact.find((h) => h.pageNumber === targetPage);

      if (targetHighlight && containerRef.current) {
        const scrollY = calculateScrollPosition(targetHighlight);
        containerRef.current.scrollTo({ top: Math.max(0, scrollY), behavior: 'smooth' });
      }
    }, [uniquePagesForCurrentFact, currentFactHighlightIndex, factHighlightsForCurrentFact, calculateScrollPosition]);

    const handleSelectionComplete = useCallback(
      (boundingBox: BoundingBox, imageData: string, selectionBox: SelectionBox, pageNumber: number) => {
        const pageElement = document.querySelector(`[data-page-number="${pageNumber}"]`);

        if (pageElement && containerRef.current) {
          const pageRect = pageElement.getBoundingClientRect();
          const containerRect = containerRef.current.getBoundingClientRect();

          // Determine side based on PDF viewer position relative to viewport
          const viewportWidth = window.innerWidth;
          const pdfViewerLeftEdge = containerRect.left;
          const isLeftHalf = pdfViewerLeftEdge < viewportWidth * 0.5;
          const side = isLeftHalf ? 'right' : 'left';

          // Position anchor based on determined side
          const anchorX =
            side === 'right'
              ? pageRect.left - containerRect.left + selectionBox.endX // Right edge of selection
              : pageRect.left - containerRect.left + selectionBox.startX; // Left edge of selection

          const anchorY =
            pageRect.top - containerRect.top + selectionBox.startY + (selectionBox.endY - selectionBox.startY) / 2;

          setPopupData({
            isOpen: true,
            imageBase64: imageData,
            boundingBox,
            anchorPosition: { x: anchorX, y: anchorY, pageNumber },
            side,
          });
          setActiveSelection({ box: selectionBox, pageNumber });
          setIsSelectionMode(false);
        }
      },
      [],
    );

    const handlePopupClose = useCallback(() => {
      setPopupData({
        isOpen: false,
        imageBase64: '',
        boundingBox: null,
        anchorPosition: null,
        side: 'left',
      });
      setActiveSelection(null);
    }, []);

    const handleFactExtractionSuccess = useCallback(() => {
      // Refresh facts or show success message
      handlePopupClose();
    }, [handlePopupClose]);

    // Search functions
    const handleSearch = useCallback((query: string) => {
      setSearchQuery(query);
      setCurrentMatchIndex(0);
    }, []);

    const navigateToMatch = useCallback(
      (targetIndex: number) => {
        if (totalSearchMatches > 0 && targetIndex >= 0 && targetIndex < totalSearchMatches) {
          setCurrentMatchIndex(targetIndex);

          // Get the current chunk and highlight all its bounding boxes
          const currentChunk = searchChunks[targetIndex];
          if (currentChunk && currentChunk.boundingBoxIds.length > 0) {
            setHighlightedChunkId(currentChunk.chunkId);
            // Scroll to the first bounding box of this chunk
            const firstBoxId = currentChunk.boundingBoxIds[0];
            const firstBox = allHighlights.find((h) => h.id === firstBoxId);
            if (firstBox && containerRef.current) {
              const scrollY = calculateScrollPosition(firstBox);
              containerRef.current.scrollTo({ top: Math.max(0, scrollY), behavior: 'smooth' });
            }
          }
        }
      },
      [totalSearchMatches, searchChunks, allHighlights, calculateScrollPosition],
    );

    const handleNextMatch = useCallback(() => {
      if (totalSearchMatches > 0) {
        const safeCurrentIndex = Math.min(currentMatchIndex, totalSearchMatches - 1);
        const nextIndex = (safeCurrentIndex + 1) % totalSearchMatches;
        navigateToMatch(nextIndex);
      }
    }, [currentMatchIndex, totalSearchMatches, navigateToMatch]);

    const handlePreviousMatch = useCallback(() => {
      if (totalSearchMatches > 0) {
        const safeCurrentIndex = Math.min(currentMatchIndex, totalSearchMatches - 1);
        const prevIndex = safeCurrentIndex === 0 ? totalSearchMatches - 1 : safeCurrentIndex - 1;
        navigateToMatch(prevIndex);
      }
    }, [currentMatchIndex, totalSearchMatches, navigateToMatch]);

    const handleCloseSearch = useCallback(() => {
      setShowSearchBar(false);
      setSearchQuery('');
      setCurrentMatchIndex(0);
      setHighlightedChunkId(null);
    }, []);

    // Scroll to first search result when search matches are loaded
    useEffect(() => {
      if (searchQuery && totalSearchMatches > 0 && currentMatchIndex === 0) {
        navigateToMatch(0);
      }
    }, [searchQuery, totalSearchMatches, currentMatchIndex, navigateToMatch]);

    // Expose clearSearch method to parent
    useImperativeHandle(
      ref,
      () => ({
        clearSearch: () => {
          handleCloseSearch();
        },
      }),
      [handleCloseSearch],
    );

    if (!matterId) {
      return null;
    }

    return (
      <>
        <div className="absolute top-0 right-0 left-0 z-30 m-2.5 flex items-start justify-between gap-1.5">
          <div className="flex flex-wrap gap-1.5">
            <Zoom handleZoomOut={handleZoomOut} handleZoomIn={handleZoomIn} />
            {!fileUrl && (
              <SearchButton showSearchBar={showSearchBar} onClick={() => setShowSearchBar(!showSearchBar)} />
            )}
            {hasBeenConvertedToImages && !hideSnippingTool && (
              <ExtractFactsFromSelectionButton
                isSelectionMode={isSelectionMode}
                setIsSelectionMode={setIsSelectionMode}
                docId={docId}
              />
            )}
            {!fileUrl && (
              <DownloadIcon
                matterId={matterId}
                docId={docId}
                fileName={fileName}
                isProcessed={doc?.isProcessed}
                fileExtension={file?.extension}
              />
            )}
            {!hideFeatureButtons && showFacts !== undefined && setShowFacts !== undefined && (
              <>
                <FullDocLink docId={docId} />
                <ShowFactsIcon showFacts={showFacts} setShowFacts={setShowFacts} />
              </>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2.5">
            <AnimatePresence mode="wait">
              {showSearchBar && !fileUrl ? (
                <motion.div
                  key="search-bar"
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.15 }}
                >
                  <SearchBar
                    onSearch={handleSearch}
                    onNext={handleNextMatch}
                    onPrevious={handlePreviousMatch}
                    onClose={handleCloseSearch}
                    currentMatch={Math.min(currentMatchIndex + 1, totalSearchMatches)}
                    totalMatches={totalSearchMatches}
                    initialQuery={initialSearchQuery}
                  />
                </motion.div>
              ) : highlightedFactId ? (
                <motion.div
                  key="fact-info"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.15 }}
                  className="flex items-center gap-2 rounded-lg border border-gray-300 bg-gray-50 p-1.5 pl-3 text-xs text-gray-700 shadow-lg/3"
                >
                  <Icon className="lucide--info text-teal-500" />
                  <div className="whitespace-nowrap">
                    Fact sourced from <span className="font-medium">{uniquePagesForCurrentFact.length}</span>{' '}
                    {uniquePagesForCurrentFact.length === 1 ? 'page' : 'pages'}{' '}
                    <span className="text-gray-400">
                      ({currentFactHighlightIndex + 1}/{uniquePagesForCurrentFact.length})
                    </span>
                  </div>

                  <div>
                    <ButtonGroup>
                      <Button
                        size="iconSm"
                        className="bg-white hover:border-gray-400 hover:bg-white"
                        onClick={handlePreviousFactHighlight}
                      >
                        <Icon className="lucide--arrow-up" />
                      </Button>
                      <Button
                        size="iconSm"
                        className="bg-white hover:border-gray-400 hover:bg-white"
                        onClick={handleNextFactHighlight}
                      >
                        <Icon className="lucide--arrow-down" />
                      </Button>
                    </ButtonGroup>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>

            {closeViewer && <CloseIcon onClick={closeViewer} className="relative m-0" />}
          </div>
        </div>

        <div
          ref={containerRef}
          className="relative h-full overflow-auto bg-gray-100 leading-[0]"
          onClick={onViewerClick}
        >
          <div className="inline-block min-h-full min-w-full bg-gray-100 pt-5 pb-3">
            {isDocLoading ? (
              <DocViewerLoader />
            ) : (
              <>
                {hasBeenConvertedToImages ? (
                  <>
                    <ImageRenderer
                      docId={docId}
                      getPageDimensions={getPageDimensions}
                      visiblePages={debouncedVisiblePages}
                      highlights={allHighlights}
                      getPagePosition={getPagePosition}
                      highlightedFactId={highlightedFactId}
                      onHighlightClick={onHighlightClick}
                      highlightedChunkId={highlightedChunkId}
                      totalHeight={totalHeight}
                      maxPageWidth={maxPageWidth}
                      isSelectionMode={isSelectionMode}
                      onSelectionComplete={handleSelectionComplete}
                      maintainSelection={popupData.isOpen}
                      activeSelection={activeSelection}
                    />
                  </>
                ) : (
                  <PDFDocumentRenderer
                    docId={docId}
                    getPageDimensions={getPageDimensions}
                    visiblePages={visiblePages}
                    highlights={allHighlights}
                    getPagePosition={getPagePosition}
                    highlightedFactId={highlightedFactId}
                    onHighlightClick={onHighlightClick}
                    highlightedChunkId={highlightedChunkId}
                    totalHeight={totalHeight}
                    maxPageWidth={maxPageWidth}
                    fileUrl={fileUrl}
                    onDocumentLoadSuccess={setLoadedFileUrlPageCount}
                    onPageLoadSuccess={handlePdfPageLoadSuccess}
                  />
                )}
              </>
            )}
          </div>
        </div>
        {popupData.isOpen && popupData.boundingBox && popupData.anchorPosition && (
          <div
            style={{
              position: 'absolute',
              left: popupData.anchorPosition.x,
              top: popupData.anchorPosition.y,
              width: 1,
              height: 1,
              pointerEvents: 'none',
              zIndex: 50,
            }}
          >
            <ExtractFactFromImagePopup
              isOpen={popupData.isOpen}
              onClose={handlePopupClose}
              docId={docId}
              imageBase64={popupData.imageBase64}
              boundingBox={popupData.boundingBox}
              onSuccess={handleFactExtractionSuccess}
              side={popupData.side}
            />
          </div>
        )}
      </>
    );
  },
);
